const createStorefrontClient = () => {
    const executeQuery = async (query, variables = {}) => {
        const cfg = window.STOREFRONT_CONFIG || {};
        const requestUrl = `https://${cfg.storeDomain}/api/${cfg.apiVersion}/graphql`;
        const res = await fetch(requestUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': cfg.publicAccessToken
            },
            body: JSON.stringify({ query, variables })
        });

        if (!res.ok) {
            const text = await res.text();
            throw new Error(`HTTP ${res.status} ${res.statusText}: ${text}`);
        }

        const json = await res.json();
        if (json.errors && json.errors.length) {
            throw new Error(json.errors[0].message || 'GraphQL error');
        }
        return json;
    };

    return {
        cart: {
            create: async (input) => {
                const mutation = `
                    mutation cartCreate($input: CartInput!) {
                        cartCreate(input: $input) {
                            cart { id checkoutUrl }
                            userErrors { field message }
                        }
                    }
                `;
                return executeQuery(mutation, { input });
            }
        }
    };
};

const CARE_ELIGIBILITY_ERROR_MESSAGE = 'Sorry, your device was activated over 48 hours ago and is not eligible for this service.';
const CARE_SN_FORMAT_ERROR_MESSAGE = 'Invalid SN format. Please enter a valid 20-character alphanumeric SN.';
/** 与 gx-hub SnValidationStatus 对齐；分支逻辑只依赖 data.validationStatus，reason 仅作展示。 */
const CARE_SN_STATUS = {
    PASS: 'PASS',
    NO_ACTIVATION: 'NO_ACTIVATION',
    NOT_ELIGIBLE: 'NOT_ELIGIBLE',
    SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE'
};
const isServiceUnavailablePayload = (data) => {
    const s = String(data?.validationStatus ?? '').trim().toUpperCase();
    return s === CARE_SN_STATUS.SERVICE_UNAVAILABLE;
};
const CARE_TRANSIENT_HINT = 'We could not verify your device SN right now (network or service issue). You can still try to continue; eligibility may be confirmed later.';
const CARE_SN_REQUIRED_MESSAGE = 'Please enter your Device SN';
const CARE_SN_VERIFIED_MESSAGE = 'Your device SN has been verified. You can continue to purchase Care.';
const CARE_SN_FEEDBACK_STYLE_ID = 'care-sn-feedback-styles';

const transientError = () => {
    const err = new Error(CARE_TRANSIENT_HINT);
    err.isTransient = true;
    return err;
};

const formatCareErrorMessage = (message) => {
    const text = (message || '').trim();
    return text || 'Validation failed';
};

const ensureCareFeedbackStyles = () => {
    if (document.getElementById(CARE_SN_FEEDBACK_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = CARE_SN_FEEDBACK_STYLE_ID;
    style.textContent = `#care-error.care-sn-feedback--error,#care-error.care-sn-feedback--success{display:block!important;padding:.75rem 1rem;border-radius:8px;font-size:.875rem;line-height:1.45}#care-error.care-sn-feedback--error{background:rgba(220,38,38,.1);color:#b91c1c;border:1px solid rgba(220,38,38,.35)}#care-error.care-sn-feedback--success{background:rgba(22,163,74,.12);color:#166534;border:1px solid rgba(22,163,74,.45)}`;
    document.head.appendChild(style);
};

const clearCareFeedback = () => {
    const el = document.getElementById('care-error');
    if (!el) return;
    el.innerText = '';
    el.classList.remove('care-sn-feedback--error', 'care-sn-feedback--success');
    el.removeAttribute('role');
    el.removeAttribute('aria-live');
    el.style.removeProperty('display');
};

const renderCareError = (message) => {
    const el = document.getElementById('care-error');
    if (!el) return false;
    ensureCareFeedbackStyles();
    const text = formatCareErrorMessage(message);
    if (!text) {
        clearCareFeedback();
        return true;
    }
    el.innerText = text;
    el.classList.remove('care-sn-feedback--success');
    el.classList.add('care-sn-feedback--error');
    el.setAttribute('role', 'alert');
    el.removeAttribute('aria-live');
    el.style.setProperty('display', 'block', 'important');
    return true;
};

const renderCareSuccess = (message) => {
    const el = document.getElementById('care-error');
    if (!el) return false;
    ensureCareFeedbackStyles();
    const cfg = window.CARE_CONFIG || {};
    const fromParam = message != null && String(message).trim() !== '';
    const text = (fromParam ? String(message).trim() : String(
        cfg.snVerifiedText || cfg.sn_verified_text || CARE_SN_VERIFIED_MESSAGE
    ).trim()) || CARE_SN_VERIFIED_MESSAGE;
    el.innerText = text;
    el.classList.remove('care-sn-feedback--error');
    el.classList.add('care-sn-feedback--success');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.style.setProperty('display', 'block', 'important');
    return true;
};

const clearCareError = () => {
    clearCareFeedback();
};

const notifyError = (message, formEl) => {
    if (!message) return;
    const displayMessage = formatCareErrorMessage(message);
    if (formEl) {
        const host = formEl.closest('.shopify-section, main, body') || document;
        host.querySelectorAll('.banner.banner--error[role="alert"]').forEach((node) => node.remove());
        formEl.dispatchEvent(new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: displayMessage }
        }));
        return;
    }
    if (window.showToast) {
        window.showToast(displayMessage, 'error');
    } else {
        alert(displayMessage);
    }
};

const getActiveProductForm = () => {
    return document.querySelector('form[is="product-form"]')
        || document.querySelector('form[action*="/cart/add"]')
        || document.querySelector('product-form form')
        || null;
};

const CARE_SN_GLOBAL_OVERLAY_ID = 'care-sn-global-overlay';
const CARE_SN_GLOBAL_STYLE_ID = 'care-sn-global-overlay-styles';
let careSnGlobalLoadDepth = 0;

const ensureCareSnGlobalOverlayStyles = () => {
    if (document.getElementById(CARE_SN_GLOBAL_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = CARE_SN_GLOBAL_STYLE_ID;
    style.textContent = `#${CARE_SN_GLOBAL_OVERLAY_ID}{position:fixed;inset:0;z-index:600000;display:flex;align-items:center;justify-content:center;padding:1.5rem;background:rgba(0,0,0,.35);backdrop-filter:blur(4px)}#${CARE_SN_GLOBAL_OVERLAY_ID}[hidden]{display:none!important}#${CARE_SN_GLOBAL_OVERLAY_ID} .care-sn-go{display:flex;flex-direction:column;align-items:center;gap:1rem;padding:2rem 2.5rem;border-radius:8px;background:rgb(var(--dialog-background,245 245 245));color:rgb(var(--text-color,26 26 26));box-shadow:0 18px 50px rgba(0,0,0,.18);max-width:20rem;text-align:center}#${CARE_SN_GLOBAL_OVERLAY_ID} .loader-dots{display:flex;gap:6px}#${CARE_SN_GLOBAL_OVERLAY_ID} .loader-dots>*{width:6px;height:6px;border-radius:50%;background:currentColor;opacity:.35;animation:care-sn-dot .45s ease-in-out infinite alternate}#${CARE_SN_GLOBAL_OVERLAY_ID} .loader-dots>*:nth-child(2){animation-delay:.1s}#${CARE_SN_GLOBAL_OVERLAY_ID} .loader-dots>*:nth-child(3){animation-delay:.2s}@keyframes care-sn-dot{to{opacity:.15;transform:translateY(1px)}}#${CARE_SN_GLOBAL_OVERLAY_ID} .care-sn-go p{margin:0;font-size:.875rem;font-weight:500;line-height:1.4}`;
    document.head.appendChild(style);
};

const showCareSnGlobalLoading = () => {
    ensureCareSnGlobalOverlayStyles();
    careSnGlobalLoadDepth += 1;
    if (careSnGlobalLoadDepth !== 1) return;

    const cfg = window.CARE_CONFIG || {};
    const labelText = String(
        cfg.snValidatingText || cfg.sn_validating_text || 'Verifying device…'
    ).trim() || 'Verifying device…';

    let el = document.getElementById(CARE_SN_GLOBAL_OVERLAY_ID);
    if (!el) {
        el = document.createElement('div');
        el.id = CARE_SN_GLOBAL_OVERLAY_ID;
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        el.setAttribute('aria-busy', 'true');
        el.innerHTML = `<div class="care-sn-go"><div class="loader-dots" aria-hidden="true"><span></span><span></span><span></span></div><p></p></div>`;
        document.body.appendChild(el);
    }
    const label = el.querySelector('.care-sn-go p');
    if (label) label.textContent = labelText;
    el.removeAttribute('hidden');
};

const hideCareSnGlobalLoading = () => {
    careSnGlobalLoadDepth = Math.max(0, careSnGlobalLoadDepth - 1);
    if (careSnGlobalLoadDepth > 0) return;
    const el = document.getElementById(CARE_SN_GLOBAL_OVERLAY_ID);
    if (el) {
        el.setAttribute('hidden', '');
        el.setAttribute('aria-busy', 'false');
        el.remove();
    }
};

const validateSnByApi = async (sn) => {
    const cfg = window.CARE_CONFIG || {};
    const baseUrl = String(cfg.validationApiBaseUrl || cfg.validation_api_base_url || '').replace(/\/$/, '');
    // 生产走 Care App Proxy；本地直连路径与 Care 服务的 context-path 对齐
    const validateUrl = baseUrl
        ? `${baseUrl}/care-service/sn/validate`
        : '/apps/skyrover-care/sn/validate';

    showCareSnGlobalLoading();
    try {
        const response = await fetch(validateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sn })
        }).catch(() => null);
        if (!response || !response.ok) throw transientError();

        let payload;
        try {
            payload = await response.json();
        } catch {
            throw transientError();
        }

        const data = payload?.data ?? null;
        if (!data) throw transientError();

        if (!data.valid) {
            const rawStatus = String(data.validationStatus ?? '').trim().toUpperCase();
            if (!rawStatus || isServiceUnavailablePayload(data)) throw transientError();
            const reason = String(data.reason || '').trim();
            const err = new Error(reason || CARE_ELIGIBILITY_ERROR_MESSAGE);
            if (rawStatus === CARE_SN_STATUS.NO_ACTIVATION || rawStatus === CARE_SN_STATUS.NOT_ELIGIBLE) {
                err.careStatus = rawStatus;
            }
            throw err;
        }
    } finally {
        hideCareSnGlobalLoading();
    }
};

const CARE_SN_VALIDATION_CACHE = {
    sn: '',
    passed: false,
    failed: false,
    transient: false,
    message: '',
    validationStatus: ''
};

const careSnEnsureSharedByKey = new Map();

const resetCareValidationCache = () => {
    CARE_SN_VALIDATION_CACHE.sn = '';
    CARE_SN_VALIDATION_CACHE.passed = false;
    CARE_SN_VALIDATION_CACHE.failed = false;
    CARE_SN_VALIDATION_CACHE.transient = false;
    CARE_SN_VALIDATION_CACHE.message = '';
    CARE_SN_VALIDATION_CACHE.validationStatus = '';
};

const setValidationSuccess = (normalizedSn) => {
    CARE_SN_VALIDATION_CACHE.sn = normalizedSn;
    CARE_SN_VALIDATION_CACHE.passed = true;
    CARE_SN_VALIDATION_CACHE.failed = false;
    CARE_SN_VALIDATION_CACHE.transient = false;
    CARE_SN_VALIDATION_CACHE.message = '';
    CARE_SN_VALIDATION_CACHE.validationStatus = '';
};

const setValidationFailed = (normalizedSn, message, careStatus) => {
    CARE_SN_VALIDATION_CACHE.sn = normalizedSn;
    CARE_SN_VALIDATION_CACHE.passed = false;
    CARE_SN_VALIDATION_CACHE.failed = true;
    CARE_SN_VALIDATION_CACHE.transient = false;
    CARE_SN_VALIDATION_CACHE.message = formatCareErrorMessage(message || 'SN validation failed');
    CARE_SN_VALIDATION_CACHE.validationStatus = String(careStatus || '').trim().toUpperCase();
};

const setValidationTransient = (normalizedSn) => {
    CARE_SN_VALIDATION_CACHE.sn = normalizedSn;
    CARE_SN_VALIDATION_CACHE.passed = false;
    CARE_SN_VALIDATION_CACHE.failed = false;
    CARE_SN_VALIDATION_CACHE.transient = true;
    CARE_SN_VALIDATION_CACHE.message = CARE_TRANSIENT_HINT;
    CARE_SN_VALIDATION_CACHE.validationStatus = '';
};

const ensureSnValidation = async (snVal) => {
    const normalizedSn = (snVal || '').trim().toUpperCase();
    if (!normalizedSn) {
        throw new Error(CARE_SN_REQUIRED_MESSAGE);
    }
    if (!/^[A-Za-z0-9]{20}$/.test(normalizedSn)) {
        throw new Error(CARE_SN_FORMAT_ERROR_MESSAGE);
    }

    if (CARE_SN_VALIDATION_CACHE.sn === normalizedSn && CARE_SN_VALIDATION_CACHE.passed) {
        return;
    }
    if (CARE_SN_VALIDATION_CACHE.sn === normalizedSn && CARE_SN_VALIDATION_CACHE.failed) {
        const cachedErr = new Error(CARE_SN_VALIDATION_CACHE.message || 'SN validation failed');
        if (CARE_SN_VALIDATION_CACHE.validationStatus) {
            cachedErr.careStatus = CARE_SN_VALIDATION_CACHE.validationStatus;
        }
        throw cachedErr;
    }
    if (CARE_SN_VALIDATION_CACHE.sn === normalizedSn && CARE_SN_VALIDATION_CACHE.transient) {
        return;
    }

    try {
        await validateSnByApi(normalizedSn);
    } catch (e) {
        if (e && e.isTransient) {
            setValidationTransient(normalizedSn);
        }
        throw e;
    }
    setValidationSuccess(normalizedSn);
};

const ensureSnValidationShared = (snVal) => {
    const normalized = (snVal || '').trim().toUpperCase();
    const inflight = careSnEnsureSharedByKey.get(normalized);
    if (inflight) return inflight;
    const p = ensureSnValidation(snVal).finally(() => {
        if (careSnEnsureSharedByKey.get(normalized) === p) {
            careSnEnsureSharedByKey.delete(normalized);
        }
    });
    careSnEnsureSharedByKey.set(normalized, p);
    return p;
};

const isCurrentSnBlocked = (snVal) => {
    const normalizedSn = (snVal || '').trim().toUpperCase();
    if (!normalizedSn) return false;
    return CARE_SN_VALIDATION_CACHE.sn === normalizedSn && CARE_SN_VALIDATION_CACHE.failed;
};

const runCarePreflight = async (snInputNode) => {
    const snVal = (snInputNode.value || '').trim();
    if (!snVal) {
        throw new Error(CARE_SN_REQUIRED_MESSAGE);
    }
    if (!/^[A-Za-z0-9]{20}$/.test(snVal)) {
        throw new Error(CARE_SN_FORMAT_ERROR_MESSAGE);
    }

    const snNorm = snVal.toUpperCase();
    let hadTransientHint = false;
    try {
        await ensureSnValidationShared(snVal);
    } catch (err) {
        if (err && err.isTransient) {
            renderCareError(CARE_TRANSIENT_HINT);
            hadTransientHint = true;
        } else {
            const message = err?.message || CARE_ELIGIBILITY_ERROR_MESSAGE;
            setValidationFailed(snVal.toUpperCase(), message, err?.careStatus);
            throw err;
        }
    }

    if (!hadTransientHint && CARE_SN_VALIDATION_CACHE.sn === snNorm && CARE_SN_VALIDATION_CACHE.transient) {
        hadTransientHint = true;
    }

    if (isCurrentSnBlocked(snVal)) {
        throw new Error(CARE_SN_VALIDATION_CACHE.message || CARE_ELIGIBILITY_ERROR_MESSAGE);
    }

    if (!hadTransientHint) {
        if (CARE_SN_VALIDATION_CACHE.sn === snNorm && CARE_SN_VALIDATION_CACHE.passed) {
            renderCareSuccess();
        } else {
            clearCareError();
        }
    }
};

const syncCarePropertiesHiddenIntoForm = (productForm, snInput) => {
    if (!productForm || !snInput) return;
    productForm.querySelectorAll('input[name="properties[Device SN]"]').forEach((n) => n.remove());

    const hiddenSn = document.createElement('input');
    hiddenSn.type = 'hidden';
    hiddenSn.name = 'properties[Device SN]';
    hiddenSn.value = snInput.value.trim();
    productForm.appendChild(hiddenSn);
};

let careLastPointerDownPath = [];

const rememberCarePointerDownPath = (event) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    careLastPointerDownPath = path.filter((node) => node && node.nodeType === Node.ELEMENT_NODE);
    window.setTimeout(() => {
        careLastPointerDownPath = [];
    }, 1000);
};

['pointerdown', 'mousedown', 'touchstart'].forEach((eventName) => {
    document.addEventListener(eventName, rememberCarePointerDownPath, true);
});

const isCareBlurSkipTarget = (target) => {
    const rt = target;
    if (!rt || rt.nodeType !== Node.ELEMENT_NODE || typeof rt.closest !== 'function') return false;
    return !!(
        rt.closest('custom-checkout-button')
        || rt.closest('buy-now-btn')
        || rt.closest('button[name="add"]')
        || rt.closest('form[action*="/cart/add"] button[type="submit"]')
        || rt.closest('[aria-controls]')
        || rt.closest('[is="close-button"], button[is="close-button"], [data-action="close"]')
        || rt.closest('x-drawer, dialog-element, navigation-drawer, .drawer, .popover')
        || rt.closest('a, button, summary, input, select, textarea, [role="button"], [tabindex]')
    );
};

const shouldSkipCareBlurValidation = (relatedTarget) => (
    isCareBlurSkipTarget(relatedTarget) || careLastPointerDownPath.some(isCareBlurSkipTarget)
);

const focusAfterCareBlurError = (snInput, dateInput, err) => {
    snInput.focus();
};

const setupCareSnBlurValidation = () => {
    const snInput = document.getElementById('care-sn');
    if (!snInput || snInput.dataset.careBlurBound === '1') return;
    snInput.dataset.careBlurBound = '1';
    const dateInput = document.getElementById('care-date');
    if (dateInput) {
        const dateContainer = dateInput.closest('.field, .form-control, .v-stack, .care-date-wrap, .care-date-container') || dateInput.parentElement;
        if (dateContainer) dateContainer.style.display = 'none';
        else dateInput.style.display = 'none';
    }

    snInput.addEventListener('input', () => {
        resetCareValidationCache();
        clearCareError();
    });

    snInput.addEventListener('blur', (event) => {
        if (shouldSkipCareBlurValidation(event.relatedTarget)) return;

        const snVal = (snInput.value || '').trim();
        if (!snVal) return;

        void (async () => {
            try {
                await runCarePreflight(snInput);
            } catch (err) {
                renderCareError(formatCareErrorMessage(err?.message));
                focusAfterCareBlurError(snInput, null, err);
            }
        })();
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupCareSnBlurValidation);
} else {
    setupCareSnBlurValidation();
}

(function setupCareNativeAddToCartOnce() {
    let careNativeAddBypass = false;
    document.addEventListener('click', (event) => {
        if (careNativeAddBypass) return;

        const snInput = document.getElementById('care-sn');
        if (!snInput) return;

        const addToCartBtn = event.target.closest('button[name="add"], form[action*="/cart/add"] button[type="submit"]');
        if (!addToCartBtn || addToCartBtn.closest('custom-checkout-button')) return;

        const productForm = addToCartBtn.closest('form[action*="/cart/add"]')
            || document.querySelector('form[action*="/cart/add"]');
        if (!productForm) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        void (async () => {
            try {
                await runCarePreflight(snInput);
            } catch (err) {
                const message = formatCareErrorMessage(err?.message);
                renderCareError(message);
                snInput.focus();
                return;
            }

            syncCarePropertiesHiddenIntoForm(productForm, snInput);
            try {
                if (typeof productForm.requestSubmit === 'function') {
                    productForm.requestSubmit(addToCartBtn);
                } else {
                    careNativeAddBypass = true;
                    try {
                        addToCartBtn.click();
                    } finally {
                        careNativeAddBypass = false;
                    }
                }
            } catch {
                /* requestSubmit / click edge cases */
            }
        })();
    }, true);
})();

const defineCheckoutElement = (name, ctor) => {
    if (typeof window !== 'undefined' && window.customElements && !window.customElements.get(name)) {
        window.customElements.define(name, ctor);
    }
};

defineCheckoutElement('buy-now-btn', class extends HTMLElement {
    constructor() {
        super();
        this.client = createStorefrontClient();
        this.isProcessing = false;
        this.init();
    }

    init() {
        this.addEventListener('click', this.handleClick.bind(this));
        this.setAttribute('role', 'button');
        this.setAttribute('tabindex', '0');
        this.style.cursor = 'pointer';
        this.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.handleClick(e);
            }
        });
    }

    async handleClick(e) {
        e.preventDefault();
        if (this.isProcessing) return;

        try {
            this.isProcessing = true;
            this.setLoadingState(true);

            const variantIdRaw = this.dataset.variantId;
            const quantity = parseInt(this.dataset.variantQuantity || '1', 10);
            if (!variantIdRaw) throw new Error('Missing product variant ID');
            if (!quantity || quantity < 1) throw new Error('Invalid product quantity');

            const merchandiseId = variantIdRaw.startsWith('gid://shopify/ProductVariant/')
                ? variantIdRaw
                : `gid://shopify/ProductVariant/${variantIdRaw}`;

            const cartInput = { lines: [{ merchandiseId, quantity }] };
            const res = await this.client.cart.create(cartInput);
            const userErrors = res.data?.cartCreate?.userErrors || [];
            if (userErrors.length) {
                throw new Error(userErrors[0].message || 'Failed to create cart');
            }

            const checkoutUrl = res.data?.cartCreate?.cart?.checkoutUrl;
            if (!checkoutUrl) throw new Error('Unable to retrieve checkout link');
            window.location.href = checkoutUrl;
        } catch (err) {
            this.handleError(err);
        } finally {
            this.isProcessing = false;
            this.setLoadingState(false);
        }
    }

    handleError(error) {
        const message = error?.message || 'An unknown error occurred. Please try again later.';
        if (renderCareError(message)) return;
        if (window.showToast) {
            window.showToast(message, 'error');
        } else {
            alert(message);
        }
    }

    setLoadingState(isLoading) {
        if (isLoading) {
            this.setAttribute('data-loading', 'true');
            this.style.pointerEvents = 'none';
            this.style.opacity = '0.7';
            if (this.dataset.loadingText) {
                this._originalText = this.textContent;
                this.textContent = this.dataset.loadingText;
            }
        } else {
            this.removeAttribute('data-loading');
            this.style.pointerEvents = '';
            this.style.opacity = '';
            if (this._originalText) {
                this.textContent = this._originalText;
                delete this._originalText;
            }
        }
    }
});

defineCheckoutElement('custom-checkout-button', class extends HTMLElement {
    constructor() {
        super();
        this.client = createStorefrontClient();
        this.isProcessing = false;
        this.init();
    }

    init() {
        this.addEventListener('click', this.handleClick.bind(this));
        this.setAttribute('role', 'button');
        this.setAttribute('tabindex', '0');
        this.style.cursor = 'pointer';
        this.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.handleClick(e);
            }
        });
    }

    async handleClick(e) {
        e.preventDefault();
        if (window.Shopify && Shopify.designMode) {
            const msg = 'Please test the checkout redirect in a real storefront preview.';
            if (!renderCareError(msg)) {
                const form = this.closest('form') || getActiveProductForm();
                notifyError(msg, form);
            }
            return;
        }

        if (this.isProcessing) return;

        try {
            this.isProcessing = true;
            this.setLoadingState(true);

            const form = this.closest('form') || getActiveProductForm();
            if (!form) throw new Error('Product form not found');
            const formId = form.getAttribute('id');

            const snInputNode = document.getElementById('care-sn');

            if (snInputNode) {
                try {
                    await runCarePreflight(snInputNode);
                } catch (err) {
                    renderCareError(formatCareErrorMessage(err?.message));
                    snInputNode.focus();
                    return;
                }
            }

            const idInput = form.querySelector('[name="id"]');
            const variantIdRaw = idInput && idInput.value;
            if (!variantIdRaw) throw new Error('Missing main product variant ID');

            const quantityInput = document.querySelector(`[name="quantity"][form="${formId}"]`) || form.querySelector('[name="quantity"]');
            const quantity = parseInt((quantityInput && quantityInput.value) ? quantityInput.value : '1', 10);

            const cleanVariantId = variantIdRaw.replace('gid://shopify/ProductVariant/', '');
            const items = [];
            const mainItem = { id: cleanVariantId, quantity: quantity || 1, properties: {} };

            if (snInputNode && snInputNode.value.trim() !== '') {
                mainItem.properties['Device SN'] = snInputNode.value.trim();
            }
            if (Object.keys(mainItem.properties).length === 0) {
                delete mainItem.properties;
            }
            items.push(mainItem);

            const giftButtons = document.querySelectorAll('.free-gift_item[data-product-id]');
            giftButtons.forEach((btn) => {
                const giftVariantId = btn.getAttribute('data-product-id');
                if (!giftVariantId) return;
                const cleanGiftId = giftVariantId.replace('gid://shopify/ProductVariant/', '');
                items.push({ id: cleanGiftId, quantity: quantity || 1 });
            });

            const selectedCare = document.querySelector('input[name="care_selection"]:checked');
            if (selectedCare && selectedCare.value === 'add') {
                const careOptionCard = selectedCare.closest('.nx-care-card, .care-option-card');
                if (careOptionCard) {
                    const careVariantId = careOptionCard.getAttribute('data-care-id');
                    if (careVariantId) {
                        const cleanCareId = careVariantId.replace('gid://shopify/ProductVariant/', '');
                        items.push({ id: cleanCareId, quantity: 1 });
                    }
                }
            }

            const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items })
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.description || 'Checkout request failed. Please refresh and try again.');
            }

            window.location.href = '/checkout';
        } catch (err) {
            this.handleError(err);
        } finally {
            this.isProcessing = false;
            this.setLoadingState(false);
        }
    }

    handleError(error) {
        const message = error?.message || 'An unknown error occurred. Please try again later.';
        if (renderCareError(message)) return;
        const form = this.closest('form') || getActiveProductForm();
        notifyError(message, form);
    }

    setLoadingState(isLoading) {
        if (isLoading) {
            this.setAttribute('data-loading', 'true');
            this.style.pointerEvents = 'none';
            this.style.opacity = '0.7';
            if (this.dataset.loadingText) {
                this._originalText = this.textContent;
                this.textContent = this.dataset.loadingText;
            }
        } else {
            this.removeAttribute('data-loading');
            this.style.pointerEvents = '';
            this.style.opacity = '';
            if (this._originalText) {
                this.textContent = this._originalText;
                delete this._originalText;
            }
        }
    }
});
